import React, { useEffect, useState } from "react"
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { format } from "date-fns"
import { useGetClanEventsTOHQuery } from "../../core/api/toh-clans-api"

const ACTION_OPTIONS = ["alliance_join", "alliance_left", "alliance_expel"]

const columns = [
    {
        field: "timestamp",
        headerName: "Timestamp",
        width: 180,
        sortable: false,
        valueGetter: (value) => {
            if (!value) return "-"
            try {
                return format(new Date(value), "dd.MM.yyyy HH:mm:ss")
            } catch {
                return String(value)
            }
        },
    },
    {
        field: "action",
        headerName: "Action",
        width: 160,
        sortable: false,
        renderCell: ({ value }) => (
            <Chip label={value || "-"} size="small" variant="outlined" />
        ),
    },
    {
        field: "userId",
        headerName: "User ID",
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (value) => value || "-",
    },
    {
        field: "expelledUserId",
        headerName: "Expelled User ID",
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (value) => value || "—",
    },
]

function MembershipHistoryTOH({ defaultClanId = "" }) {
    const [clanId, setClanId] = useState(defaultClanId || "")
    const [clanIdTouched, setClanIdTouched] = useState(false)
    const [actions, setActions] = useState([...ACTION_OPTIONS])
    const [userId, setUserId] = useState("")
    const [expelledUserId, setExpelledUserId] = useState("")
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 })

    useEffect(() => {
        if (!clanIdTouched) {
            setClanId(defaultClanId || "")
        }
    }, [defaultClanId, clanIdTouched])

    const resetPage = () => setPaginationModel((m) => ({ ...m, page: 0 }))

    const handleClanIdChange = (v) => {
        setClanId(v)
        setClanIdTouched(true)
        resetPage()
    }

    const handleActionsChange = (event) => {
        const value = event.target.value
        const next = typeof value === "string" ? value.split(",") : value
        if (next.length === 0) return
        setActions(next)
        resetPage()
    }

    const handleUserIdChange = (v) => {
        setUserId(v)
        resetPage()
    }

    const handleExpelledUserIdChange = (v) => {
        setExpelledUserId(v)
        resetPage()
    }

    const trimmedClanId = clanId.trim()
    const trimmedUserId = userId.trim()
    const trimmedExpelledUserId = expelledUserId.trim()
    const hasClanId = !!trimmedClanId
    const hasActions = actions.length > 0
    const skip = !hasClanId || !hasActions

    const { data, isLoading, isFetching } = useGetClanEventsTOHQuery(
        {
            allianceId: trimmedClanId,
            actions,
            userId: trimmedUserId || undefined,
            expelledUserId: trimmedExpelledUserId || undefined,
            page: paginationModel.page,
            limit: paginationModel.pageSize,
        },
        { skip }
    )

    const rows = Array.isArray(data)
        ? data.map((row, index) => ({
            ...row,
            id: `${paginationModel.page}-${index}-${row.timestamp}-${row.action}-${row.userId}-${row.expelledUserId ?? ""}`,
        }))
        : []

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Membership History
                </Typography>

                <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 2, flexWrap: "wrap" }}>
                    <TextField
                        label="Clan ID"
                        size="small"
                        required
                        value={clanId}
                        onChange={(e) => handleClanIdChange(e.target.value)}
                        helperText={
                            defaultClanId
                                ? "Auto-filled from current player's clan"
                                : "Enter a Clan ID"
                        }
                        sx={{ minWidth: 280 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 280 }} required>
                        <InputLabel>Action</InputLabel>
                        <Select
                            multiple
                            label="Action"
                            value={actions}
                            onChange={handleActionsChange}
                            input={<OutlinedInput label="Action" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((v) => (
                                        <Chip key={v} label={v} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {ACTION_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="User ID"
                        size="small"
                        value={userId}
                        onChange={(e) => handleUserIdChange(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                    <TextField
                        label="Expelled User ID"
                        size="small"
                        value={expelledUserId}
                        onChange={(e) => handleExpelledUserIdChange(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                </Stack>

                {!hasClanId ? (
                    <Alert severity="info">Enter a Clan ID to view membership history</Alert>
                ) : !hasActions ? (
                    <Alert severity="warning">Select at least one action</Alert>
                ) : (
                    <Box sx={{ width: "100%", height: 560 }}>
                        <DataGridPro
                            disableColumnMenu
                            disableColumnSorting
                            disableRowSelectionOnClick
                            pagination
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[100]}
                            rowCount={-1}
                            loading={isLoading || isFetching}
                            columns={columns}
                            rows={rows}
                            getRowHeight={() => "auto"}
                            sx={{
                                "& .MuiDataGrid-cell": {
                                    py: 1,
                                    alignItems: "center",
                                },
                            }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

export default MembershipHistoryTOH
